import { isDataURL, isRemoteURL, mediaFileName, type HermesAPI, type MediaRef } from "@timibot/core";

/**
 * 服务器上的文件（生成的图片/视频、上传的图片等）在浏览器里的缓存（对应 iOS 的 MediaStore）。
 * 走 `/api/fs/download`（经加密隧道，带登录凭据），下载后变成 blob: 地址给 <img>/<video> 用。
 */
class MediaCache {
  private blobs = new Map<string, Blob>();
  private urls = new Map<string, string>();
  private inflight = new Map<string, Promise<string>>();

  /** 自己刚上传的文件：直接用本机这份，不用再下载 */
  seed(source: string, blob: Blob): void {
    this.blobs.set(source, blob);
  }

  cachedURL(ref: MediaRef): string | undefined {
    if (isRemoteURL(ref) || isDataURL(ref)) return ref.source;
    return this.urls.get(ref.source) ?? this.makeURL(ref.source);
  }

  private makeURL(source: string): string | undefined {
    const blob = this.blobs.get(source);
    if (!blob) return undefined;
    const url = URL.createObjectURL(blob);
    this.urls.set(source, url);
    return url;
  }

  /** 可以直接放进 <img src> 的地址 */
  async url(ref: MediaRef, api: HermesAPI | undefined): Promise<string> {
    const hit = this.cachedURL(ref);
    if (hit) return hit;
    const pending = this.inflight.get(ref.source);
    if (pending) return pending;
    const task = (async () => {
      if (!api) throw new Error("还没有连接到主机");
      const { data, mime } = await api.downloadServerFile(ref.source);
      const blob = new Blob([data as Uint8Array<ArrayBuffer>], { type: mime || guessMime(ref) });
      this.blobs.set(ref.source, blob);
      return this.makeURL(ref.source)!;
    })().finally(() => this.inflight.delete(ref.source));
    this.inflight.set(ref.source, task);
    return task;
  }

  /** 下载到用户电脑（浏览器的“保存文件”） */
  async save(ref: MediaRef, api: HermesAPI | undefined): Promise<void> {
    const url = await this.url(ref, api);
    const a = document.createElement("a");
    a.href = url;
    a.download = mediaFileName(ref);
    if (isRemoteURL(ref)) { a.target = "_blank"; a.rel = "noopener"; }
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

function guessMime(ref: MediaRef): string {
  const name = mediaFileName(ref).toLowerCase();
  const ext = name.split(".").pop() ?? "";
  const map: Record<string, string> = {
    png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp", bmp: "image/bmp",
    mp4: "video/mp4", mov: "video/quicktime", m4v: "video/mp4", webm: "video/webm",
    mp3: "audio/mpeg", m4a: "audio/mp4", wav: "audio/wav", ogg: "audio/ogg", pdf: "application/pdf",
    txt: "text/plain", md: "text/markdown", csv: "text/csv", json: "application/json",
  };
  return map[ext] ?? "application/octet-stream";
}

export const mediaCache = new MediaCache();
